import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Eye, EyeSlash, WarningCircle } from "@phosphor-icons/react";
import { customerAuth } from "../lib/customerAuth";

/**
 * /customer/reset-password?token=...
 * ----------------------------------
 * - Sets Referrer-Policy: no-referrer via meta so the URL (with token)
 *   is not leaked to any third party.
 * - Never logs / posts the raw token anywhere.
 */
export default function CustomerResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // Belt & braces: refresh the referrer policy for this page load.
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => { try { document.head.removeChild(meta); } catch { /* noop */ } };
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.(); if (busy) return;
    setErr("");
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    if (pw.length < 12) { setErr("Password must be at least 12 characters."); return; }
    if (pw.length > 256) { setErr("Password must be at most 256 characters."); return; }
    setBusy(true);
    try {
      await customerAuth.reset(token, pw);
      setPw(""); setPw2("");
      try { sessionStorage.setItem("gf_login_notice", "Your password has been reset successfully. Sign in with your new password."); } catch { /* noop */ }
      nav("/customer/login");
    } catch (ex) {
      setPw(""); setPw2("");
      setErr("Invalid or expired reset link.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="customer-reset-page">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-lg border border-slate-200">
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="text-xl font-semibold text-slate-900">Set a new password</div>
          <div className="text-xs text-slate-500 mt-1">Choose a strong password — 12 to 256 characters.</div>
        </div>
        <div className="px-6 py-5 space-y-3">
          {!token ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex items-start gap-2" data-testid="customer-reset-notoken">
              <WarningCircle size={16} weight="bold" className="mt-[1px] shrink-0" />
              <span>Reset link is missing. Please open the link you received.</span>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="customer-reset-form">
              {err && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex items-start gap-2" data-testid="customer-reset-error" role="alert">
                  <WarningCircle size={16} weight="bold" className="mt-[1px] shrink-0" /><span>{err}</span>
                </div>
              )}
              <PasswordField id="crp-pw"  label="New password"     value={pw}  onChange={setPw}  show={show} setShow={setShow} testid="customer-reset-new" />
              <PasswordField id="crp-pw2" label="Confirm password" value={pw2} onChange={setPw2} show={show} setShow={setShow} testid="customer-reset-confirm" />
              <ul className="text-xs text-slate-500 list-disc pl-5 space-y-0.5">
                <li>12–256 characters</li>
                <li>Spaces and Unicode allowed</li>
                <li>No arbitrary complexity rules — long passphrases welcome</li>
              </ul>
              <button
                type="submit"
                disabled={busy}
                data-testid="customer-reset-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
              >
                <CheckCircle size={16} weight="bold" /> {busy ? "Resetting…" : "Reset password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordField({ id, label, value, onChange, show, setShow, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-700 block mb-1" htmlFor={id}>{label}</label>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          autoComplete="new-password"
          required
          minLength={12}
          maxLength={256}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full rounded-lg border border-slate-300 text-sm px-3 py-2 pr-9 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        <button type="button" onClick={() => setShow(v => !v)}
                aria-label={show ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800">
          {show ? <EyeSlash size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}
